import { useEffect, useState } from "react";
import { useAppInsights } from "render/context/AppInsightsContext";
import Notification, { NotificationType } from "./Notification";

/**
 * The last resort when a request fails and no call site tells the clinician.
 *
 * A clinician on a phone mid-visit reads an unchanged screen as the device having stalled, and
 * waits. This says otherwise, in the one case a specific message is not coming.
 *
 * Listens for the platform's `unhandledrejection` event rather than for the API client's
 * `onError`. The client reports every failed request, including the ones a call site catches
 * and describes itself; the platform reports only the rejections nobody caught, which is the
 * exact set this component exists for.
 */
const MESSAGE =
  "Something went wrong. Reload the page, and check any image you just added appears before you move on.";

const QUIET: NotificationType = { type: "error", message: "" };

function asError(reason: unknown) {
  return reason instanceof Error ? reason : new Error(String(reason));
}

export default function UnexpectedErrorNotice() {
  const appInsights = useAppInsights();
  const [notification, setNotification] = useState<NotificationType>(QUIET);

  useEffect(() => {
    function onUnhandledRejection(event: PromiseRejectionEvent) {
      console.error("Unhandled rejection:", event.reason);
      appInsights.trackException({
        exception: asError(event.reason),
        properties: { source: "unhandledrejection" },
      });
      setNotification({ type: "error", message: MESSAGE });
    }

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [appInsights]);

  return (
    <Notification
      notification={notification}
      setNotification={setNotification}
    />
  );
}
